<template>
  <div id="jci-program-payment-report" class="premium-dashboard">
    <!-- Background Floating Light Orbs -->
    <div class="glow-orb orb-1"></div>
    <div class="glow-orb orb-2"></div>

    <!-- Massive Hero Dashboard Header -->
    <v-card class="hero-card mb-6" flat>
      <div class="hero-glow-effect"></div>

      <!-- Floating Decorative Ambient Icons -->
      <v-icon class="deco-icon icon-card" color="rgba(255, 255, 255, 0.08)"
        >mdi-receipt-outline</v-icon
      >
      <v-icon class="deco-icon icon-receipt" color="rgba(255, 255, 255, 0.08)"
        >mdi-currency-inr</v-icon
      >
      <v-icon class="deco-icon icon-safe" color="rgba(255, 255, 255, 0.08)"
        >mdi-shield-check-outline</v-icon
      >

      <!-- Ocean Wave Background Animation -->
      <div class="waves-container">
        <svg
          class="waves"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          viewBox="0 24 150 28"
          preserveAspectRatio="none"
          shape-rendering="auto"
        >
          <defs>
            <path
              id="gentle-wave"
              d="M-160 44c30 0 58-18 88-18s58 18 88 18 58-18 88-18 58 18 88 18v44h-352z"
            />
          </defs>
          <g class="parallax">
            <use
              xlink:href="#gentle-wave"
              x="48"
              y="0"
              fill="rgba(255, 255, 255, 0.05)"
            />
            <use
              xlink:href="#gentle-wave"
              x="48"
              y="3"
              fill="rgba(255, 255, 255, 0.08)"
            />
            <use
              xlink:href="#gentle-wave"
              x="48"
              y="5"
              fill="rgba(255, 255, 255, 0.03)"
            />
            <use
              xlink:href="#gentle-wave"
              x="48"
              y="7"
              fill="rgba(255, 255, 255, 0.07)"
            />
          </g>
        </svg>
      </div>

      <br />
      <v-row class="ma-0 align-center relative-content">
        <v-col cols="12" md="6" class="pa-6 pb-12">
          <div class="d-flex align-center title-container">
            <v-icon large color="white" class="mr-3 title-icon pulse-animation"
              >mdi-receipt</v-icon
            >
            <h1 class="hero-title animate-title">
              {{ PageTitle || "JCI Program Payment Report" }}
            </h1>
          </div>
          <p class="hero-subtitle animate-subtitle">
            Manage, track, and migrate event payment registrations seamlessly.
          </p>
        </v-col>
        <v-col
          cols="12"
          md="6"
          class="pa-6 pb-12 text-md-right d-none d-sm-block"
        >
          <v-btn
            @click.prevent="openMigrationDialog"
            color="white"
            light
            elevation="3"
            rounded
            large
            class="font-weight-bold mr-3"
          >
            <v-icon left color="primary">mdi-swap-horizontal-bold</v-icon>
            MIGRATE
          </v-btn>
          <v-btn
            v-if="RoleId == 2"
            @click.prevent="addRecordPrompt = true"
            color="white"
            light
            elevation="3"
            rounded
            large
            class="font-weight-bold mr-3"
          >
            <v-icon left color="primary">mdi-plus</v-icon>
            ADD PAYMENT
          </v-btn>
          <v-btn
            large
            @click.prevent="refreshPageData"
            color="rgba(255,255,255,0.2)"
            dark
            elevation="0"
            rounded
            class="font-weight-bold"
            style="border: 1px solid rgba(255, 255, 255, 0.4)"
          >
            <v-icon left>mdi-refresh</v-icon>
            Refresh
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <v-dialog v-model="addRecordPrompt" persistent max-width="600px">
      <add-event-online-payment
        pageDescription="Add Event Online Payment"
        :addRecordPrompt="addRecordPrompt"
        @hideAddRecordPrompt="hideAddRecordPrompt"
        v-if="addRecordPrompt"
      ></add-event-online-payment>
    </v-dialog>
    <v-dialog
      v-model="transferEventPrompt"
      persistent
      scrollable
      max-width="65%"
    >
      <transfer-jci-event
        :transferEventPrompt="transferEventPrompt"
        :recordData="selectedData"
        @hidetransferEventPrompt="hidetransferEventPrompt($event)"
        v-if="transferEventPrompt"
      >
      </transfer-jci-event>
    </v-dialog>

    <!-- Summary Statistics Row -->
    <v-row class="mb-6" v-if="tableData1.length > 0">
      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card">
          <div class="stat-card-glow orb-total"></div>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">Total Payments</span>
              <h2 class="stat-value">{{ tableData1.length }}</h2>
            </div>
            <v-avatar color="rgba(21, 101, 192, 0.1)" size="48">
              <v-icon color="#1565C0">mdi-receipt</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card">
          <div class="stat-card-glow orb-success"></div>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">Approved</span>
              <h2 class="stat-value">
                {{
                  tableData1.filter(
                    (item) => item.ActiveStatusTxt === "Approved"
                  ).length
                }}
              </h2>
            </div>
            <v-avatar color="rgba(76, 175, 80, 0.1)" size="48">
              <v-icon color="#4caf50">mdi-check-circle-outline</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card">
          <div class="stat-card-glow orb-rev"></div>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">Total Pax</span>
              <h2 class="stat-value">
                {{
                  tableData1.reduce(
                    (sum, item) => sum + (item.OrderQty || 1),
                    0
                  )
                }}
              </h2>
            </div>
            <v-avatar color="rgba(30, 136, 229, 0.1)" size="48">
              <v-icon color="#1e88e5">mdi-account-group-outline</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card">
          <div class="stat-card-glow orb-pending"></div>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">Upgraded / Other</span>
              <h2 class="stat-value">
                {{
                  tableData1.filter(
                    (item) => item.ActiveStatusTxt !== "Approved"
                  ).length
                }}
              </h2>
            </div>
            <v-avatar color="rgba(255, 152, 0, 0.1)" size="48">
              <v-icon color="#ff9800">mdi-swap-horizontal</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters Section -->
    <v-card class="filter-card glass-card mb-6">
      <div class="filter-card-header d-flex align-center">
        <v-icon color="white" class="mr-2">mdi-filter-variant</v-icon>
        <h3 class="filter-card-title">Search & Filter Parameters</h3>
      </div>

      <v-card-text class="pa-6">
        <!-- Loading -->
        <div v-if="LoadingFlag" class="text-center py-8">
          <v-progress-circular
            :size="60"
            :width="6"
            color="primary"
            indeterminate
          ></v-progress-circular>
          <p class="mt-3 text-subtitle-1 primary--text font-weight-bold">
            Loading payment data...
          </p>
        </div>

        <v-form
          ref="form2"
          v-model="valid1"
          lazy-validation
          v-if="!LoadingFlag"
        >
          <v-row wrap align="center">
            <v-col cols="12" sm="6" lg="3" md="3" class="py-2">
              <label class="filter-label"
                ><span class="text-danger">* </span>Year</label
              >
              <v-autocomplete
                :reduce="(option) => option.value"
                :loading="JciYearCodeOptionsLoading"
                :items="JciYearCodeOptions"
                :rules="JciYearCodeRules"
                v-model="JciYearCode"
                placeholder="Select Year"
                dense
                clearable
                required
                outlined
                rounded
                class="premium-select"
                prepend-inner-icon="mdi-calendar-multiple"
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" sm="6" lg="3" md="3" class="py-2">
              <label class="filter-label"
                >Event Name <small>(optional)</small></label
              >
              <v-autocomplete
                :reduce="(option) => option.value"
                :loading="EventCodeOptionsLoading"
                :items="EventCodeOptions"
                :rules="EventCodeRules"
                v-model="EventCode"
                placeholder="Select Event"
                dense
                clearable
                required
                outlined
                rounded
                class="premium-select"
                prepend-inner-icon="mdi-calendar-check"
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" sm="6" lg="3" md="3" class="py-2">
              <label class="filter-label"
                >Payment Id <small>(optional)</small></label
              >
              <v-text-field
                v-model="PaymentId"
                :rules="PaymentIdRules"
                placeholder="Razorpay Payment Id"
                required
                dense
                outlined
                rounded
                class="premium-select"
                prepend-inner-icon="mdi-credit-card-multiple"
              ></v-text-field>
            </v-col>

            <v-col cols="12" sm="6" lg="3" md="3" class="py-2">
              <label class="filter-label">Zone <small>(optional)</small></label>
              <v-autocomplete
                :reduce="(option) => option.value"
                :loading="ZoneCodeOptionsLoading"
                :items="ZoneCodeOptions"
                :rules="ZoneCodeRules"
                v-model="ZoneCode"
                placeholder="Select Zone"
                dense
                clearable
                required
                outlined
                rounded
                class="premium-select"
                prepend-inner-icon="mdi-map-marker-radius"
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" sm="6" lg="3" md="3" class="py-2">
              <label class="filter-label">LO <small>(optional)</small></label>
              <v-autocomplete
                :reduce="(option) => option.value"
                :loading="LomCodeOptionsLoading"
                :items="LomCodeOptions"
                :rules="LomCodeRules"
                v-model="LomCode"
                placeholder="Select LO"
                dense
                clearable
                required
                outlined
                rounded
                class="premium-select"
                prepend-inner-icon="mdi-office-building-marker"
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" sm="6" lg="3" md="3" class="py-2">
              <label class="filter-label">From Date</label>
              <v-menu
                v-model="menu1"
                :close-on-content-click="false"
                :nudge-right="40"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    required
                    v-model="FromDate"
                    placeholder="From Date"
                    readonly
                    v-on="on"
                    dense
                    outlined
                    rounded
                    class="premium-select"
                    prepend-inner-icon="mdi-calendar-range"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="FromDate"
                  @input="menu1 = false"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="12" sm="6" lg="3" md="3" class="py-2">
              <label class="filter-label">To Date</label>
              <v-menu
                v-model="menu2"
                :close-on-content-click="false"
                :nudge-right="40"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    required
                    v-model="ToDate"
                    placeholder="To Date"
                    readonly
                    v-on="on"
                    dense
                    outlined
                    rounded
                    class="premium-select"
                    prepend-inner-icon="mdi-calendar-range"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="ToDate"
                  @input="menu2 = false"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="12" sm="6" lg="3" md="3" class="py-2 pt-6 text-right">
              <v-btn
                :disabled="!valid1"
                @click.prevent="searchForm"
                :loading="SearchFlag"
                rounded
                large
                class="filter-btn btn-search white--text px-8"
              >
                <v-icon left>mdi-magnify</v-icon>
                SEARCH
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- Empty State -->
    <v-card
      v-if="!StartupLoadingFlag && ResultFlag && tableData1.length == 0"
      class="glass-card pa-8 text-center mb-6"
    >
      <v-icon color="#1565C0" size="64" class="mb-3"
        >mdi-database-search</v-icon
      >
      <h3 class="text-h6 font-weight-bold gray--text text--darken-2">
        No payment records found matching criteria.
      </h3>
      <p class="text-caption text--secondary mb-0">
        Try adjusting your filter settings or search query.
      </p>
    </v-card>

    <!-- Data Table Card -->
    <v-card
      v-if="tableData1.length > 0"
      class="table-container-card glass-card mb-6"
    >
      <v-card-title
        class="px-6 py-4 d-flex justify-content-between align-center flex-wrap"
      >
        <div class="d-flex align-center mb-2 mb-sm-0">
          <v-icon color="#1565C0" class="mr-2">mdi-database</v-icon>
          <span class="result-count-text"
            >{{ tableData1.length }} Payment Records Found</span
          >
        </div>

        <div class="d-flex align-center flex-wrap">
          <v-btn
            v-if="
              tableData1.length > 0 && tableOptions1.DownloadButtonFlag == true
            "
            @click.prevent="
              generateExcel(
                tableData1,
                tableOptions1.ExcelFields,
                tableOptions1.ExcelFileName,
                'My Worksheet'
              )
            "
            color="#20B2AA"
            dark
            rounded
            small
            class="mr-3 font-weight-bold"
          >
            <v-icon left small>mdi-file-excel</v-icon>
            Export Excel
          </v-btn>

          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            placeholder="Quick search table..."
            single-line
            hide-details
            dense
            outlined
            rounded
            class="global-table-search"
          ></v-text-field>
        </div>
      </v-card-title>

      <v-card-text class="pa-0">
        <v-data-table
          class="premium-table"
          loading-text="Loading... Please wait"
          v-model="selected"
          :search="search"
          :loading="TableLoadingFlag"
          :item-key="tableOptions1.ItemKey"
          :single-select="tableOptions1.SingleSelectFlag"
          :headers="tableColumns1"
          :items="tableData1"
          :items-per-page="tableOptions1.ItemsPerPage"
          :footer-props="tableOptions1.FooterProps"
        >
          <template v-slot:[`item.ActiveStatusTxt`]="{ item }">
            <v-chip
              :color="item.ActiveStatusColor"
              draggable
              dark
              class="font-weight-bold status-chip"
              >{{ item.ActiveStatusTxt }}</v-chip
            >
          </template>
          <template v-slot:[`item.ProfilePic`]="{ item }">
            <v-avatar size="48" class="payment-avatar">
              <img :src="item.ProfilePic" alt="Profile" />
            </v-avatar>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <div class="action-buttons">
              <v-tooltip bottom v-if="item.ApprovalFlag">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-if="item.ApprovalFlag"
                    @click="previewData(item)"
                    target="_blank"
                    icon
                    small
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    class="action-btn-view"
                  >
                    <v-icon small> mdi-check-circle </v-icon>
                  </v-btn>
                </template>
                <span> Approve Registration </span>
              </v-tooltip>
              <v-tooltip bottom v-if="item.ReceiptFilePath != ''">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :href="item.ReceiptFilePath"
                    target="_blank"
                    icon
                    small
                    color="success"
                    v-bind="attrs"
                    v-on="on"
                    class="action-btn-view"
                  >
                    <v-icon small> mdi-receipt </v-icon>
                  </v-btn>
                </template>
                <span> Receipt Download </span>
              </v-tooltip>
              <v-tooltip bottom v-if="!item.NonMemberFlag">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    @click="transferToOtherEvent(item)"
                    target="_blank"
                    icon
                    small
                    color="warning"
                    v-bind="attrs"
                    v-on="on"
                    class="action-btn-view"
                  >
                    <v-icon small> mdi-account-convert </v-icon>
                  </v-btn>
                </template>
                <span> Member transfer </span>
              </v-tooltip>
              <v-tooltip bottom v-if="item.ApplicationPath != ''">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :href="item.ApplicationPath"
                    target="_blank"
                    icon
                    small
                    color="error"
                    v-bind="attrs"
                    v-on="on"
                    class="action-btn-view"
                  >
                    <v-icon small> mdi-file-pdf </v-icon>
                  </v-btn>
                </template>
                <span> Application Download </span>
              </v-tooltip>
              <v-tooltip bottom v-if="item.MemberImagePath != ''">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :href="item.MemberImagePath"
                    target="_blank"
                    icon
                    small
                    color="info"
                    v-bind="attrs"
                    v-on="on"
                    class="action-btn-view"
                  >
                    <v-icon small> mdi-image-area </v-icon>
                  </v-btn>
                </template>
                <span> Member Image Download </span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="previewRecordPrompt" max-width="75%">
      <jci-program-payment-approval
        pageDescription="Event Registration Approval"
        @hideDialog="hidePreviewRecordPrompt($event)"
        :row="selectedData"
        v-if="previewRecordPrompt"
      ></jci-program-payment-approval>
    </v-dialog>

    <!-- Migration Dialog -->
    <v-dialog
      v-model="migrationDialogPrompt"
      persistent
      scrollable
      max-width="950px"
    >
      <v-card class="premium-checkout-card">
        <!-- Checkout Banner Header -->
        <div class="checkout-banner">
          <div class="hero-glow-effect"></div>
          <v-icon class="deco-icon icon-card" color="rgba(255, 255, 255, 0.08)"
            >mdi-swap-horizontal-bold</v-icon
          >
          <v-row class="ma-0 align-center relative-content">
            <v-col cols="9" class="text-left pa-0">
              <div class="banner-title d-flex align-center">
                <v-icon dark class="mr-2 pulse-animation" color="#EDBE38"
                  >mdi-swap-horizontal-bold</v-icon
                >
                Plan Upgrade Migration
              </div>
              <p class="banner-subtitle">
                Select eligible payment order records to migrate to Upgrade
                status.
              </p>
            </v-col>
            <v-col cols="3" class="text-right pa-0">
              <v-btn
                icon
                dark
                class="mr-1"
                @click.prevent="openMigrationDialog"
                :loading="migrationLoadingFlag"
                title="Refresh Eligible Records"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
              <v-btn icon dark @click="migrationDialogPrompt = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <v-card-text class="pt-4 card-content-area pa-6">
          <div v-if="migrationLoadingFlag" class="text-center py-8">
            <v-progress-circular
              indeterminate
              size="50"
              width="5"
              color="primary"
            ></v-progress-circular>
            <p class="mt-3 text-subtitle-1 primary--text font-weight-bold">
              Fetching eligible migration records...
            </p>
          </div>

          <div v-else>
            <div class="d-flex align-center justify-space-between mb-4">
              <div class="d-flex align-center">
                <v-chip color="primary" outlined class="font-weight-bold mr-3">
                  {{ migrationEligibleRecords.length }} Eligible Candidate(s)
                  Found
                </v-chip>
                <v-btn
                  small
                  rounded
                  outlined
                  color="primary"
                  @click.prevent="openMigrationDialog"
                  :loading="migrationLoadingFlag"
                  class="font-weight-bold"
                >
                  <v-icon left small>mdi-refresh</v-icon>
                  Refresh
                </v-btn>
              </div>
              <span class="text-caption text--secondary font-weight-bold">
                Selected: {{ selectedMigrationRows.length }} /
                {{ migrationEligibleRecords.length }}
              </span>
            </div>

            <v-alert
              v-if="migrationEligibleRecords.length === 0"
              type="info"
              colored-border
              border="left"
              elevation="2"
              class="mb-0"
            >
              No eligible records found matching migration criteria (Member with
              2 orders, dated 14-Aug-2026 with old plan reference).
            </v-alert>

            <div v-else>
              <v-data-table
                :headers="migrationHeaders"
                :items="migrationEligibleRecords"
                v-model="selectedMigrationRows"
                show-select
                item-key="OrdersId"
                class="premium-table elevation-1 rounded-lg border"
                dense
              >
                <template v-slot:item.PlanTypeTxt="{ item }">
                  <v-chip
                    :color="item.PlanTypeId == 2 ? 'success' : 'warning'"
                    x-small
                    dark
                    label
                    class="font-weight-bold"
                  >
                    {{ item.PlanTypeTxt }}
                  </v-chip>
                </template>
              </v-data-table>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="pa-4 bg-light border-top d-flex justify-end">
          <v-btn
            @click.prevent="openMigrationDialog"
            :loading="migrationLoadingFlag"
            rounded
            large
            outlined
            class="filter-btn btn-clear mr-3"
          >
            <v-icon left>mdi-refresh</v-icon>
            Refresh
          </v-btn>
          <v-btn
            @click="migrationDialogPrompt = false"
            rounded
            large
            outlined
            class="filter-btn btn-clear mr-3"
          >
            Cancel
          </v-btn>
          <v-btn
            :loading="migrationSubmitLoading"
            :disabled="selectedMigrationRows.length === 0"
            @click="submitMigration"
            rounded
            large
            class="filter-btn btn-search white--text px-8"
          >
            <v-icon left>mdi-check-circle-outline</v-icon>
            Submit / Migrate ({{ selectedMigrationRows.length }})
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
  ADD_BODY_CLASSNAME,
  REMOVE_BODY_CLASSNAME,
} from "@/core/services/store/htmlclass.module.js";
import AddEventOnlinePayment from "@/view/pages/erp/reports/jci-progrm/AddJcomOnlinePayment.vue";
import TransferJciEvent from "@/view/pages/erp/reports/jci-progrm/TransferJciEvent";
import JciProgramPaymentApproval from "@/view/pages/erp/reports/jci-progrm/JciProgramPaymentApproval.vue";
export default {
  mixins: [common],
  components: {
    AddEventOnlinePayment,
    TransferJciEvent,
    JciProgramPaymentApproval,
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      TableLoadingFlag: false,
      StartupLoadingFlag: false,
      FloatingButtonFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      ResultFlag: false,

      rows: [],
      tableData1: [],
      tableColumns1: [],
      tableOptions1: [],
      selected: [],
      search: "",
      CurrentYearId: 0,
      selectedData: {},

      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",

      addRecordPrompt: false,
      editRecmordPrompt: false,
      previewRecordPrompt: false,
      transferEventPrompt: false,

      migrationDialogPrompt: false,
      migrationLoadingFlag: false,
      migrationSubmitLoading: false,
      migrationEligibleRecords: [],
      selectedMigrationRows: [],
      migrationHeaders: [],

      zone: {},
      lom: {},

      name: "",
      nameRules: [
        (v) => !!v || "Name is required",
        (v) => (v && v.length <= 10) || "Name must be less than 10 characters",
      ],
      email: "",
      emailRules: [
        (v) => !!v || "E-mail is required",
        (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
      ],
      select: "Item 1",
      items: ["Item 1", "Item 2", "Item 3", "Item 4"],
      checkbox: false,

      JciYearCodeRules: [(v) => !!v || "Year is required"],
      JciYearCode: "",
      JciYearCodeOptions: [],
      JciYearCodeOptionsLoading: false,

      EventCodeRules: [],
      EventCode: "",
      EventCodeOptions: [],
      EventCodeOptionsLoading: false,

      PaymentId: "",
      PaymentIdRules: [],

      ZoneCodeRules: [],
      ZoneCode: "",
      ZoneCodeOptions: [],
      ZoneCodeOptionsLoading: false,

      LomCodeRules: [],
      LomCode: "",
      LomCodeOptions: [],
      LomCodeOptionsLoading: false,

      FromDate: "",
      FromDateRules: [(v) => !!v || "From date is required"],
      menu1: false,

      ToDate: "",
      ToDateRules: [(v) => !!v || "To date is required"],
      menu2: false,

      SearchId: "",
      SearchIdRules: [(v) => !!v || "Search Type is required"],
      SearchIdOptions: [
        { value: "", text: "--Select Type--" },
        { value: 1, text: "By LO" },
        { value: 2, text: "By Membership Id" },
      ],

      MembershipId: "",
      MembershipIdRules: [(v) => !!v || "Membership Id is required"],

      YearHalfId: "",
      YearHalfIdRules: [(v) => !!v || "Year Half is required"],
      YearHalfIdOptions: [
        { value: "", text: "--Select Half--" },
        { value: "First", text: "First Half" },
        { value: "Second", text: "Second Half" },
      ],

      YearIdRules: [(v) => !!v || "Year is required"],
      YearId: "",
      YearIdOptions: [],
      YearIdOptionsLoading: false,

      ApprovalDescription: "",
      ApprovalDescriptionRules: [],

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
      FloatingButtonOptions: {},
      RoleId: "",
    };
  },
  mounted() {
    /*
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Vuetify", route: "alerts" },
      { title: "Form Inputs & Control", route: "autocompletes" },
      { title: "Fileinptus" },
    ]);
    */
  },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var flag = tr.PagePermissionFlag;
        console.log("PagePermissionFlag=" + flag);

        if (!flag) {
          this.$router.push("/access-denied");
        }

        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;
        this.FloatingButtonOptions = tr.FloatingButtonOptions;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var CurrentYearId = this.$session.get("CurrentYearId");
        CurrentYearId = 1;
        CurrentYearId =
          CurrentYearId == (null || undefined) ? 0 : CurrentYearId;
        this.CurrentYearId = CurrentYearId;

        var RoleId = this.$session.get("RoleId");
        RoleId = RoleId == (null || undefined) ? "" : RoleId;
        // RoleId = 4;
        this.RoleId = RoleId;

        var todayDate = new Date().toISOString().slice(0, 10);
        console.log(todayDate);

        this.FromDate = todayDate;
        this.ToDate = todayDate;

        this.getJciYearCodeOptions();
        this.getZoneCodeOptions();
        // this.getEventCodeOptions();
      }
    },
    EventCodeOptions: function () {
      console.log("watch EventCodeOptions");
      this.EventCodeOptionsLoading = false;
    },
    ZoneCode: function () {
      console.log("watch ZoneCode");
      var ZoneCode = this.ZoneCode;
      console.log({ ZoneCode });
      this.LomCode = "";
      if (ZoneCode > 0) {
        this.getLomCodeOptions();
      }
    },
    ZoneCodeOptions: function () {
      console.log("watch ZoneCodeOptions");
      this.ZoneCodeOptionsLoading = false;
    },
    LomCodeOptions: function () {
      console.log("watch LomCodeOptions");
      this.LomCodeOptionsLoading = false;
    },
    JciYearCode: function () {
      console.log("watch JciYearCode");
      var JciYearCode = this.JciYearCode;
      console.log({ JciYearCode });
      this.EventCode = "";
      if (JciYearCode > 0) {
        this.getEventCodeOptions();
      }
    },
  },
  methods: {
    // code 1
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    hideAddRecordPrompt() {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
    },
    resetForm() {
      this.search = "";
      this.rows = [];
      this.selected = [];
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    hidePreviewRecordPrompt(flag) {
      console.log("hidePreviewRecordPrompt called");
      console.log({ flag });
      this.previewRecordPrompt = false;
      if (flag) {
        this.searchForm();
      }
    },
    previewData(tr) {
      console.log("previewData called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.previewRecordPrompt = true;
        this.selectedData = tr;
      } else {
        var message = "Kindly select one record to preview";
        this.toast("error", message);
      }
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      this.resetForm();
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "natcon_payment",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    getJciYearCodeOptions() {
      console.log("getJciYearCodeOptions called");
      var selectbox1_source = "JciYearCode";
      var selectbox1_destination = "JciYearCodeOptions";
      var selectbox1_url = "api/jci-year/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
        LomCode: 3,
        ZoneCode: 2,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getZoneCodeOptions() {
      console.log("getZoneCodeOptions called");
      var yearCode = this.JciYearCode;
      console.log("yearCode=" + yearCode);
      if (yearCode != "") {
        this.ZoneCode = "";
        this.ZoneCodeOptionsLoading = true;
        var selectbox1_source = "ZoneCode";
        var selectbox1_destination = "ZoneCodeOptions";
        var selectbox1_url = "api/year-wise-lom/zone-options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          YearCode: yearCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "";
        if (yearCode == "") {
          message += "Year should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    getEventCodeOptions() {
      console.log("getEventCodeOptions called");

      this.EventCode = "";
      this.EventCodeOptionsLoading = true;
      var selectbox1_source = "EventCode";
      var selectbox1_destination = "EventCodeOptions";
      var selectbox1_url = "api/jci-program/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
        Year: this.JciYearCode,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getLomCodeOptions() {
      console.log("getLomCodeOptions called");
      var YearCode = this.JciYearCode;
      var ZoneCode = this.ZoneCode;
      console.log("YearCode=" + YearCode + ", ZoneCode=" + ZoneCode);
      if (YearCode != "" && ZoneCode != "") {
        this.LomCodeOptionsLoading = true;
        var selectbox1_source = "LomCode";
        var selectbox1_destination = "LomCodeOptions";
        var selectbox1_url = "api/year-wise-lom/lom-options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
          YearCode: YearCode,
          ZoneCode: ZoneCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "";
        if (YearCode == "") {
          message += "Year should not be empty. ";
        }
        if (ZoneCode == "") {
          message += "Zone should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    getYearCodeOptions(source, destination) {
      console.log("getYearCodeOptions called");
      // this.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
      // var CurrentYearId = this.CurrentYearId;
      var CurrentYearId = 1;
      console.log(
        "source=" +
          source +
          ", destination=" +
          destination +
          ", CurrentYearId=" +
          CurrentYearId
      );
      if (source != "" && destination != "") {
        this.LoadingFlag = true;
        var selectbox1_source = source;
        var selectbox1_destination = destination;
        var selectbox1_url = "api/jci-year/options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    searchForm() {
      console.log("searchForm is called");

      var validate1 = this.$refs.form2.validate();
      var FromDate = this.FromDate;
      var ToDate = this.ToDate;
      console.log(
        "validate1=" +
          validate1 +
          ", FromDate=" +
          FromDate +
          ", ToDate=" +
          ToDate
      );

      if (validate1 && FromDate != "" && ToDate != "") {
        this.SearchFlag = true;
        this.tableData1 = [];
        this.zone = {};
        this.lom = {};

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/jci-program/registration/lists";
        var upload = {
          UserInterface: 1,
          Zone: this.ZoneCode,
          Lom: this.LomCode,
          Event: this.EventCode,
          PaymentId: this.PaymentId,
          FromDate: this.FromDate,
          ToDate: this.ToDate,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.SearchFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            var options = response.data.options;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            thisIns.tableColumns1 = options.TableHeader;
            thisIns.tableOptions1 = options.TableOptions;

            if (flag == 1) {
              thisIns.tableData1 = records;
              thisIns.toast("success", output);
            } else {
              // thisIns.toast("error", output);
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SearchFlag = false;
          });
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields. ";
        }
        if (FromDate == "") {
          message += "From Date should not be empty. ";
        }
        if (ToDate == "") {
          message += "To Date should not be empty. ";
        }
        // this.toast("error", message);
        this.sweetAlert("error", message, false);
      }
    },
    hidetransferEventPrompt(flag) {
      console.log("hidetransferEventPrompt called");
      console.log({ flag });
      this.transferEventPrompt = false;
      if (flag) {
        this.searchForm();
      }
    },
    transferToOtherEvent(tr) {
      console.log("transferToOtherEvent called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.selectedData = tr;
        this.transferEventPrompt = true;
      } else {
        var message = "Kindly select one member to edit";
        this.toast("error", message);
      }
    },
    openMigrationDialog() {
      console.log("openMigrationDialog called");
      this.migrationEligibleRecords = [];
      this.selectedMigrationRows = [];
      this.migrationDialogPrompt = true;
      this.migrationLoadingFlag = true;

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var url =
        server_url + "api/jci-program/registration/migration-eligible-lists";

      const thisIns = this;
      this.$http({
        url: url,
        method: "POST",
        data: { UserInterface: 1 },
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          thisIns.migrationLoadingFlag = false;
          if (response.data.flag) {
            thisIns.migrationEligibleRecords = response.data.records || [];
            if (response.data.options && response.data.options.TableHeader) {
              thisIns.migrationHeaders = response.data.options.TableHeader;
            }
          } else {
            thisIns.sweetAlert(
              "error",
              response.data.output || "Failed to fetch eligible records.",
              false
            );
          }
        })
        .catch(function (error) {
          console.log("migration-eligible-lists error=" + error);
          thisIns.migrationLoadingFlag = false;
          thisIns.sweetAlert(
            "error",
            "Error loading migration records.",
            false
          );
        });
    },
    submitMigration() {
      if (this.selectedMigrationRows.length === 0) {
        this.sweetAlert(
          "error",
          "Please select at least one record to migrate.",
          false
        );
        return;
      }

      const selectedIds = this.selectedMigrationRows.map(
        (item) => item.OrdersId
      );
      console.log("submitMigration selectedIds=", selectedIds);

      this.migrationSubmitLoading = true;
      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var url =
        server_url + "api/jci-program/registration/migrate-upgraded-orders";

      const thisIns = this;
      this.$http({
        url: url,
        method: "POST",
        data: {
          UserInterface: 1,
          SelectedOrders: selectedIds,
        },
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          thisIns.migrationSubmitLoading = false;
          if (response.data.flag) {
            thisIns.migrationDialogPrompt = false;
            thisIns.sweetAlert("success", response.data.output, true);
            thisIns.searchForm();
          } else {
            thisIns.sweetAlert(
              "error",
              response.data.output || "Migration failed.",
              false
            );
          }
        })
        .catch(function (error) {
          console.log("migrate-upgraded-orders error=" + error);
          thisIns.migrationSubmitLoading = false;
          thisIns.sweetAlert(
            "error",
            "An error occurred during migration execution.",
            false
          );
        });
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap");

.premium-dashboard {
  font-family: "Outfit", "Plus Jakarta Sans", sans-serif;
  background-color: #f6f8fc;
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  padding: 12px;
}

.premium-checkout-card {
  font-family: "Outfit", "Plus Jakarta Sans", sans-serif;
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
  border-radius: 24px !important;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(21, 101, 192, 0.18) !important;
  border: 1px solid rgba(255, 255, 255, 0.7) !important;
}

.checkout-banner {
  background: linear-gradient(135deg, #1565c0 0%, #1e88e5 50%, #42a5f5 100%);
  padding: 30px 20px;
  text-align: center;
  position: relative;
  overflow: hidden;
  color: white;
}

.banner-title {
  font-size: 20px;
  font-weight: 700;
  margin-top: 8px;
}

.banner-subtitle {
  font-size: 13px;
  opacity: 0.9;
  margin-top: 4px;
  margin-bottom: 0;
}

.card-content-area {
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.8) 0%,
    #ffffff 100%
  );
}

/* Floating light ambient background orbs */
.glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(120px);
  pointer-events: none;
  opacity: 0.45;
  z-index: 0;
}

.orb-1 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, #42a5f5 0%, rgba(21, 101, 192, 0) 70%);
  top: -100px;
  right: -50px;
}

.orb-2 {
  width: 500px;
  height: 500px;
  background: radial-gradient(
    circle,
    rgba(21, 101, 192, 0.2) 0%,
    rgba(66, 165, 245, 0) 70%
  );
  bottom: -150px;
  left: -100px;
}

/* Massive Hero Dashboard Header styling */
.hero-card {
  background: linear-gradient(
    135deg,
    #1565c0 0%,
    #1a73e8 45%,
    #42a5f5 100%
  ) !important;
  border-radius: 20px !important;
  color: white;
  box-shadow: 0 16px 40px rgba(21, 101, 192, 0.25) !important;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

.hero-title {
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.8px;
  line-height: 1.2;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.hero-subtitle {
  font-size: 14px;
  opacity: 0.95;
  margin-top: 6px;
  max-width: 600px;
  font-weight: 400;
}

/* Glassmorphism General Cards styling */
.glass-card {
  background: rgba(255, 255, 255, 0.82) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.7) !important;
  border-radius: 20px !important;
  box-shadow: 0 10px 30px rgba(33, 33, 33, 0.04) !important;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

/* Summary Statistics Cards */
.stat-card {
  padding: 24px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 15px 35px rgba(21, 101, 192, 0.12) !important;
}

.stat-card-glow {
  position: absolute;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  filter: blur(40px);
  top: -40px;
  right: -40px;
  opacity: 0.15;
}

.orb-total {
  background-color: #1565c0;
}

.orb-rev {
  background-color: #4caf50;
}

.orb-success {
  background-color: #1e88e5;
}

.orb-pending {
  background-color: #ff9800;
}

.stat-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: #78909c;
  letter-spacing: 0.8px;
}

.stat-value {
  font-size: 24px;
  font-weight: 800;
  color: #263238;
  margin-top: 4px;
  letter-spacing: -0.5px;
}

/* Advanced Filter Card header gradient */
.filter-card-header {
  background: linear-gradient(90deg, #1565c0 0%, #1e88e5 100%);
  padding: 16px 24px;
  color: white;
}

.filter-card-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 0;
}

.filter-label {
  font-size: 12px;
  font-weight: 700;
  color: #455a64;
  margin-bottom: 6px;
  display: block;
}

/* Autocomplete/Select box rounded controls */
.premium-select >>> .v-input__control > .v-input__slot {
  border-radius: 12px !important;
  border: 1.5px solid rgba(21, 101, 192, 0.1) !important;
  background-color: white !important;
  transition: all 0.3s ease !important;
}

.premium-select >>> .v-input__control > .v-input__slot:hover {
  border-color: rgba(21, 101, 192, 0.35) !important;
}

.premium-select.v-input--is-focused >>> .v-input__control > .v-input__slot {
  border-color: #1565c0 !important;
  box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.15) !important;
}

/* Filter action buttons */
.filter-btn {
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0.2px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.btn-clear {
  color: #455a64 !important;
  border: 1.5px solid rgba(69, 90, 100, 0.2) !important;
}

.btn-clear:hover {
  background-color: rgba(69, 90, 100, 0.05) !important;
  border-color: #455a64 !important;
}

.btn-search {
  background: linear-gradient(135deg, #1565c0 0%, #1e88e5 100%) !important;
  box-shadow: 0 6px 20px rgba(21, 101, 192, 0.3) !important;
  border: none !important;
}

.btn-search:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(21, 101, 192, 0.45) !important;
}

/* Skeleton Loaders and Empty States styling */
.premium-loader {
  background: transparent !important;
}

/* Data grid container and table styles */
.table-container-card {
  overflow: hidden;
}

.result-count-text {
  font-size: 16px;
  font-weight: 700;
  color: #263238;
}

/* Premium global table search text field */
.global-table-search {
  max-width: 280px;
}

.global-table-search >>> .v-input__control > .v-input__slot {
  border-radius: 12px !important;
  border: 1.5px solid rgba(21, 101, 192, 0.12) !important;
  background-color: rgba(21, 101, 192, 0.01) !important;
}

.global-table-search.v-input--is-focused
  >>> .v-input__control
  > .v-input__slot {
  border-color: #1565c0 !important;
  box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.12) !important;
  background-color: white !important;
}

/* Real premium tables */
.premium-table >>> table {
  border-collapse: separate;
  border-spacing: 0;
}

.premium-table >>> thead > tr > th {
  background-color: rgba(21, 101, 192, 0.04) !important;
  color: #1565c0 !important;
  font-weight: 700 !important;
  font-size: 15px !important;
  border-bottom: 2px solid rgba(21, 101, 192, 0.08) !important;
}

.premium-table >>> tbody > tr {
  transition: background-color 0.2s ease;
}

.premium-table >>> tbody > tr:hover {
  background-color: rgba(21, 101, 192, 0.03) !important;
}

.premium-table >>> tbody > tr > td {
  border-bottom: 1px solid rgba(21, 101, 192, 0.04) !important;
  font-size: 15px !important;
  color: #37474f;
  padding: 12px 16px !important;
}

.status-chip {
  font-weight: bold;
}

/* Ocean Wave Styling */
.waves-container {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 40px;
  min-height: 40px;
  max-height: 60px;
  z-index: 1;
}

.waves {
  position: relative;
  width: 100%;
  height: 40px;
  margin-bottom: -7px;
}

.parallax > use {
  animation: move-forever 25s cubic-bezier(0.55, 0.5, 0.45, 0.5) infinite;
}

.parallax > use:nth-child(1) {
  animation-delay: -2s;
  animation-duration: 7s;
}

.parallax > use:nth-child(2) {
  animation-delay: -3s;
  animation-duration: 10s;
}

.parallax > use:nth-child(3) {
  animation-delay: -4s;
  animation-duration: 13s;
}

.parallax > use:nth-child(4) {
  animation-delay: -5s;
  animation-duration: 20s;
}

@keyframes move-forever {
  0% {
    transform: translate3d(-90px, 0, 0);
  }

  100% {
    transform: translate3d(85px, 0, 0);
  }
}

/* Hero header animations & shapes */
.hero-glow-effect {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0) 100%
  );
  z-index: 0;
  pointer-events: none;
}

.hero-glow-effect::after {
  content: "";
  position: absolute;
  top: 0;
  left: -50%;
  width: 200%;
  height: 100%;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.15) 30%,
    rgba(255, 255, 255, 0) 60%
  );
  transform: skewX(-25deg);
  animation: shineSweep 6s infinite ease-in-out;
}

.relative-content {
  position: relative;
  z-index: 2;
}

/* Decorative background icons */
.deco-icon {
  position: absolute !important;
  z-index: 1;
  pointer-events: none;
}

.icon-card {
  font-size: 110px !important;
  right: 20%;
  top: -20px;
  transform: rotate(15deg);
  animation: floatDeco 12s infinite ease-in-out;
}

.icon-receipt {
  font-size: 80px !important;
  left: 10%;
  bottom: -15px;
  transform: rotate(-10deg);
  animation: floatDeco 15s infinite ease-in-out alternate;
}

.icon-safe {
  font-size: 90px !important;
  right: 5%;
  bottom: -10px;
  transform: rotate(20deg);
  animation: floatDeco 10s infinite ease-in-out;
}

/* Specific text and icon animations */
.animate-title {
  animation: slideInLeft 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.animate-subtitle {
  opacity: 0;
  animation: slideInLeft 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.15s forwards;
}

.pulse-animation {
  animation: pulseIcon 3s infinite ease-in-out;
}

/* Keyframes definitions */
@keyframes shineSweep {
  0% {
    left: -120%;
  }

  15%,
  100% {
    left: 120%;
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes floatDeco {
  0%,
  100% {
    transform: translateY(0) rotate(15deg) scale(1);
  }

  50% {
    transform: translateY(-12px) rotate(20deg) scale(1.03);
  }
}

@keyframes pulseIcon {
  0%,
  100% {
    transform: scale(1);
    filter: drop-shadow(0 0 0px rgba(255, 255, 255, 0));
  }

  50% {
    transform: scale(1.08);
    filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.6));
  }
}

.action-btn-view {
  transition: transform 0.2s ease;
}

.action-btn-view:hover {
  transform: scale(1.18);
  background: rgba(21, 101, 192, 0.08) !important;
}

.text-danger {
  color: #d32f2f;
}
</style>